import React, { useState, useEffect } from "react";
import editorange from "assets/images/icon/edit-orange.png";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getCompany, updateCompany } from "services/company";
import Input from "components/Input";
import Select from "components/Select";

const DEPOSIT_SCHEDULES = [
    { value: "monthly", label: "Monthly" },
    { value: "semiweekly", label: "Semi-Weekly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "annually", label: "Annually" },
];

const FILING_FREQUENCIES = [
    { value: "941", label: "Form 941 (Quarterly)" },
    { value: "944", label: "Form 944 (Annually)" },
];

const buildForm = (company) => ({
    ein: company?.ein || "",
    federalDepositSchedule: company?.federalDepositSchedule || "",
    filingFrequency: company?.filingFrequency || "",
    stateUnemploymentId: company?.stateUnemploymentId || "",
    stateWithholdingId: company?.stateWithholdingId || "",
    sutaRate: company?.sutaRate || "",
    sutaWageBaseOverride: company?.sutaWageBaseOverride || "",
});

const FederalStateTaxesSection = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [form, setForm] = useState(buildForm(null));
    const queryClient = useQueryClient();

    const { data: company, isLoading, isError } = useQuery({
        queryKey: ["company"],
        queryFn: getCompany,
    });

    useEffect(() => {
        if (company) setForm(buildForm(company));
    }, [company]);

    const mutation = useMutation({
        mutationFn: updateCompany,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["company"] });
            setIsEditing(false);
        },
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = (e) => {
        e.preventDefault();
        mutation.mutate({
            ein: form.ein,
            federalDepositSchedule: form.federalDepositSchedule,
            filingFrequency: form.filingFrequency,
            stateUnemploymentId: form.stateUnemploymentId,
            stateWithholdingId: form.stateWithholdingId,
            sutaRate: form.sutaRate,
            sutaWageBaseOverride: form.sutaWageBaseOverride || null,
        });
    };

    const handleCancel = () => {
        setForm(buildForm(company));
        setIsEditing(false);
        mutation.reset();
    };

    if (isLoading) return <p className="text-center mt-4">Loading tax information...</p>;
    if (isError) return <p className="text-danger mt-4">Failed to load tax information.</p>;

    if (isEditing) {
        return (
            <div>
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <h2 className="sub-title">Edit Federal & State Taxes</h2>
                </div>
                <form onSubmit={handleSave}>
                    <div className="border-bottom pb-4 mb-4">
                        <p className="info-title mb-3">Federal Tax Information</p>
                        <div className="row g-3">
                            <div className="col-lg-6 col-12">
                                <Input label="EIN (Employer Identification Number)" fullWidth variant="flat" name="ein" value={form.ein} onChange={handleChange} placeholder="XX-XXXXXXX" />
                            </div>
                            <div className="col-lg-6 col-12">
                                <Select label="Federal Deposit Schedule" fullWidth placeholder="Select schedule" options={DEPOSIT_SCHEDULES} name="federalDepositSchedule" value={form.federalDepositSchedule} onChange={handleChange} />
                            </div>
                            <div className="col-lg-6 col-12">
                                <Select label="Filing Frequency" fullWidth placeholder="Select frequency" options={FILING_FREQUENCIES} name="filingFrequency" value={form.filingFrequency} onChange={handleChange} />
                            </div>
                        </div>
                    </div>
                    <div className="pb-4 mb-4">
                        <p className="info-title mb-3">State Tax Information</p>
                        <div className="row g-3">
                            <div className="col-lg-6 col-12">
                                <Input label="State Withholding ID" fullWidth variant="flat" name="stateWithholdingId" value={form.stateWithholdingId} onChange={handleChange} placeholder="State withholding account number" />
                            </div>
                            <div className="col-lg-6 col-12">
                                <Input label="State Unemployment ID (SUTA)" fullWidth variant="flat" name="stateUnemploymentId" value={form.stateUnemploymentId} onChange={handleChange} placeholder="State unemployment account number" />
                            </div>
                            <div className="col-lg-6 col-12">
                                <Input label="SUTA Rate (%)" fullWidth variant="flat" name="sutaRate" type="number" step="0.01" value={form.sutaRate} onChange={handleChange} placeholder="e.g. 2.7" />
                            </div>
                            <div className="col-lg-6 col-12">
                                <Input label="SUTA Wage Base Override ($)" fullWidth variant="flat" name="sutaWageBaseOverride" type="number" step="100" value={form.sutaWageBaseOverride} onChange={handleChange} placeholder={`Leave blank to use state default`} />
                                <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Override the default state SUTA wage base (e.g. if your state has a different limit). Leave blank to use the built-in value.</p>
                            </div>
                        </div>
                    </div>
                    {mutation.isError && (
                        <p className="text-danger mb-3">
                            {mutation.error?.response?.data?.message || "Failed to save. Please try again."}
                        </p>
                    )}
                    <div className="d-flex gap-2">
                        <button type="submit" className="button-orange py-2 px-4 rounded-pill" disabled={mutation.isPending}>
                            {mutation.isPending ? "Saving..." : "Save Changes"}
                        </button>
                        <button type="button" className="btn btn-outline-secondary rounded-pill py-2 px-4" onClick={handleCancel} disabled={mutation.isPending}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between">
                <h2 className="sub-title">Federal & State Taxes</h2>
                <button className="border-0 bg-transparent" onClick={() => setIsEditing(true)}>
                    <img className="cursor-pointer" src={editorange} alt="edit" />
                </button>
            </div>

            <div className="border-bottom pb-4 mb-4">
                <p className="info-title mb-3">Federal Tax Information</p>
                <div className="general-information-section">
                    <div>
                        <p className="m-0 title">EIN</p>
                        <p className="m-0 sub-titles">{company?.ein || "—"}</p>
                    </div>
                    <div>
                        <p className="m-0 title">Federal Deposit Schedule</p>
                        <p className="m-0 sub-titles">
                            {DEPOSIT_SCHEDULES.find(s => s.value === company?.federalDepositSchedule)?.label || company?.federalDepositSchedule || "—"}
                        </p>
                    </div>
                    <div>
                        <p className="m-0 title">Filing Frequency</p>
                        <p className="m-0 sub-titles">
                            {FILING_FREQUENCIES.find(f => f.value === company?.filingFrequency)?.label || company?.filingFrequency || "—"}
                        </p>
                    </div>
                </div>
            </div>

            <div>
                <p className="info-title mb-3">State Tax Information</p>
                <div className="general-information-section">
                    <div>
                        <p className="m-0 title">State Withholding ID</p>
                        <p className="m-0 sub-titles">{company?.stateWithholdingId || "—"}</p>
                    </div>
                    <div>
                        <p className="m-0 title">State Unemployment ID (SUTA)</p>
                        <p className="m-0 sub-titles">{company?.stateUnemploymentId || "—"}</p>
                    </div>
                    <div>
                        <p className="m-0 title">SUTA Rate</p>
                        <p className="m-0 sub-titles">{company?.sutaRate ? `${company.sutaRate}%` : "—"}</p>
                    </div>
                    <div>
                        <p className="m-0 title">SUTA Wage Base Override</p>
                        <p className="m-0 sub-titles">
                            {company?.sutaWageBaseOverride
                                ? `$${Number(company.sutaWageBaseOverride).toLocaleString()} (custom)`
                                : <span style={{ color: '#aaa' }}>Using state default</span>
                            }
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FederalStateTaxesSection;
